import { InfoPage } from '@/components/info-page.js';
import { PageBreadcrumbProps } from '@/components/page-breadcrumb.js';
import { EntityTable } from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { Button } from '@/components/ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { useClassTimeOptions } from '@/lib/api-call-hooks.js';
import { EmptyArray, getSchool, getStudentGroups } from '@/lib/api-calls.js';
import { useRowsBulkSelection } from '@/lib/bulk-selection.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import { createFormOptions } from '@/lib/form-utils.js';
import { paginationRequestSchema } from '@/lib/pagination.js';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing.js';
import { useSorting } from '@/lib/sorting.js';
import { BulkUpdateClasses } from '@/routes/_layout/orgs/schools/-components/bulk-update-classes.js';
import { BulkUpdateOverrideDates } from '@/routes/_layout/orgs/schools/-components/bulk-update-override-dates.js';
import { CreateEditClass } from '@/routes/_layout/orgs/schools/-components/create-edit-class.js';
import { DeleteStudentGroup } from '@/routes/_layout/orgs/schools/-components/delete-student-group.js';
import { getSchoolClassTableColumns } from '@/routes/_layout/orgs/schools/-components/school-class-table-columns.js';
import { CreateSessionsForStudentGroups } from '@/routes/_layout/scheduling/student-groups/-components/create-sessions-for-student-groups.js';
import {
  EditStudentGroupBulkActions,
  GetStudentGroupsSortableColumn,
  getStudentGroupsSortOrderSchema,
  StudentGroupStatus,
  StudentGroupStatusLabelMap,
  studentGroupStatusSchema,
  StudentGroupWithScheduleAndLevel,
} from '@airreading/common/data';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo } from 'react';
import { z } from 'zod';
import { SchoolPageTitle } from '../../-components/school-page-title.js';
import { schoolDetailsTabs } from '../../-components/tabs.js';
import { isAirAdmin } from '@airreading/common';
import { ImportStudentGroupFromV1 } from './-import-student-group-v1.js';
import { ChevronDown } from 'lucide-react';

export const Route = createFileRoute('/_layout/orgs/schools/$schoolId/classes/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: SchoolClassesDetails,
  validateSearch: paginationRequestSchema.extend({
    query: z.string().optional(),
    scheduleSchoolId: z.array(z.string()).optional(),
    statuses: z.array(z.nativeEnum(StudentGroupStatus)).optional(),
    classTimeIds: z.array(z.string()).optional(),
    sortBy: getStudentGroupsSortOrderSchema.optional(),
  }),
});

const allStatuesFilterOptions = createFormOptions<StudentGroupStatus>(
  studentGroupStatusSchema.options,
  StudentGroupStatusLabelMap,
);

function SchoolClassesDetails() {
  const { schoolId } = Route.useParams();
  const { data } = useSuspenseQuery(getSchool(schoolId));
  const timeZone = data.org.timeZone;

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[nameQuery, activeNameQuery], setNameQuery],
      statuses: [[selectedStatuses, activeSelectedStatuses], setSelectedStatuses],
      classTimeIds: [[classTimeIds, activeClassTimeIds], setClassTimeIds],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'statuses', 'classTimeIds']);
  const [sortBy, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetStudentGroupsSortableColumn>([sortBy, didChangeSortBy]);

  const { classTimeOptions } = useClassTimeOptions({ schoolId });

  const {
    data: studentGroupsResponse,
    error,
    refetch,
    isPending,
  } = useQuery(
    getStudentGroups({
      pageIndex,
      pageSize,
      filters: {
        query: activeNameQuery,
        scheduleSchools: [data.id],
        statuses: activeSelectedStatuses?.length ? activeSelectedStatuses : [StudentGroupStatus.active],
        classTimeIds: activeClassTimeIds,
        classTimeTypes: ['recurring'],
        includeLevelChangeWarning: true,
      },
      sortBy,
    }),
  );
  const studentGroups = useMemo(() => studentGroupsResponse?.studentGroups || EmptyArray, [studentGroupsResponse]);
  useErrorToast(error, 'Error loading student groups. Please try again.');

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<StudentGroupWithScheduleAndLevel>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => studentGroups.find((studentGroup) => studentGroup.id === id),
    });

  const columns = useMemo(
    () => getSchoolClassTableColumns(schoolId, data.org.timeZone, true),
    [schoolId, data.org.timeZone],
  );
  const table = useReactTable({
    data: studentGroups ?? EmptyArray,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      sorting,
      rowSelection,
    },
    rowCount: studentGroupsResponse?.total || 0,
    getCoreRowModel: getCoreRowModel(),
    getRowId,
    onPaginationChange,
    onRowSelectionChange,
    manualPagination: true,
    ...tableSortingOptions,
  });

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search by students, teachers, class times, etc."
          value={nameQuery ?? ''}
          onChange={(event) => {
            setNameQuery(event.target.value);
          }}
        />
        <TableFilterDropdown
          title="Class time (CT)"
          options={classTimeOptions}
          selectedValues={classTimeIds ?? []}
          onChange={setClassTimeIds}
        />
        <TableFilterDropdown
          title="Status"
          options={allStatuesFilterOptions}
          onChange={setSelectedStatuses}
          selectedValues={selectedStatuses ?? []}
        />
        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [
      nameQuery,
      setNameQuery,
      classTimeOptions,
      classTimeIds,
      setClassTimeIds,
      selectedStatuses,
      setSelectedStatuses,
      resetFilters,
    ],
  );

  const breadcrumbs: PageBreadcrumbProps = useMemo(() => {
    return {
      linkItems: [
        {
          label: 'Schools',
          link: {
            to: '/orgs/schools',
          },
        },
      ],
      currentPage: data.name,
    };
  }, [data]);

  const bulkActions = useMemo(() => {
    return (
      <>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Generate sessions</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <CreateSessionsForStudentGroups
              trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Generate new sessions</DropdownMenuItem>}
              studentGroups={rowDataSelection}
              shouldSendEmail={true}
              onSuccess={resetAllRowSelection}
            />
            <BulkUpdateClasses
              studentGroups={rowDataSelection}
              trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Extend last session</DropdownMenuItem>}
              bulkType={EditStudentGroupBulkActions.ExtendLastSession}
              title="Bulk Extend Last Session"
              schoolId={schoolId}
              timeZone={timeZone}
              onSuccess={resetAllRowSelection}
            />
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Update sessions</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <BulkUpdateClasses
              studentGroups={rowDataSelection}
              trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Update teacher</DropdownMenuItem>}
              bulkType={EditStudentGroupBulkActions.UpdateTeacher}
              title="Bulk Update Teacher"
              schoolId={schoolId}
              timeZone={timeZone}
              onSuccess={resetAllRowSelection}
            />
            <BulkUpdateClasses
              studentGroups={rowDataSelection}
              trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Update lessons</DropdownMenuItem>}
              bulkType={EditStudentGroupBulkActions.UpdateLesson}
              title="Bulk Update Lesson"
              schoolId={schoolId}
              timeZone={timeZone}
              onSuccess={resetAllRowSelection}
            />
            <BulkUpdateClasses
              studentGroups={rowDataSelection}
              trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Update students</DropdownMenuItem>}
              bulkType={EditStudentGroupBulkActions.UpdateStudents}
              title="Bulk Update Students"
              schoolId={schoolId}
              timeZone={timeZone}
              onSuccess={resetAllRowSelection}
            />
            <BulkUpdateClasses
              studentGroups={rowDataSelection}
              trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Remove future sessions</DropdownMenuItem>}
              bulkType={EditStudentGroupBulkActions.RemoveFutureSessions}
              title="Bulk Remove Future Sessions"
              schoolId={schoolId}
              timeZone={timeZone}
              onSuccess={resetAllRowSelection}
            />
            <BulkUpdateClasses
              studentGroups={rowDataSelection}
              trigger={
                <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={rowDataSelection.length !== 1}>
                  Recreate sessions
                </DropdownMenuItem>
              }
              bulkType={EditStudentGroupBulkActions.RecreateSessions}
              title="Recreate sessions"
              schoolId={schoolId}
              timeZone={timeZone}
              onSuccess={resetAllRowSelection}
            />
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <BulkUpdateOverrideDates
          studentGroups={rowDataSelection}
          trigger={
            <DropdownMenuItem
              onSelect={(e) => e.preventDefault()}
              data-testid="classes-bulk-actions-button-update-override-dates"
            >
              Update override dates
            </DropdownMenuItem>
          }
          onSuccess={resetAllRowSelection}
        />
        <BulkUpdateClasses
          studentGroups={rowDataSelection}
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Merge classes</DropdownMenuItem>}
          bulkType={EditStudentGroupBulkActions.MergeClasses}
          title="Bulk Merge Classes"
          schoolId={schoolId}
          timeZone={timeZone}
          onSuccess={resetAllRowSelection}
        />
        <BulkUpdateClasses
          studentGroups={rowDataSelection}
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Archive classes</DropdownMenuItem>}
          bulkType={EditStudentGroupBulkActions.ArchiveSessions}
          title="Bulk Archive Classes"
          schoolId={schoolId}
          timeZone={timeZone}
          onSuccess={resetAllRowSelection}
        />
        <DeleteStudentGroup
          studentGroups={rowDataSelection}
          triggerTestId="classes-bulk-actions-button-delete"
          dataTestId="classes-modal-delete"
          afterDelete={resetAllRowSelection}
        />
      </>
    );
  }, [resetAllRowSelection, rowDataSelection, schoolId, timeZone]);

  return (
    <InfoPage
      headerText={<SchoolPageTitle name={data.name || data.id} isHighPriority={data.isHighPriority} />}
      breadcrumbs={breadcrumbs}
      tabs={schoolDetailsTabs({ schoolId })}
    >
      <EntityTable
        table={table}
        isLoading={isPending}
        showPagination
        emptyState={'No recurring classes found'}
        hasError={!!error}
        errorState={<TableErrorState refetchAction={refetch} />}
        bulkActionsMenuItems={bulkActions}
        autoManagedColumnIds={['selection', 'student-group-id']}
        filters={{ filterLeftComponents }}
        topBarRightComponents={
          <>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button className="ml-auto hidden h-8 lg:flex">
                  Add Class
                  <ChevronDown className="mr-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[150px]">
                <CreateEditClass
                  trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Add Class</DropdownMenuItem>}
                  schoolId={data.id}
                  key="create"
                  schoolTimeZone={timeZone}
                />
                <ImportStudentGroupFromV1
                  trigger={
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                      Import student groups from V1
                    </DropdownMenuItem>
                  }
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />
    </InfoPage>
  );
}
